import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Pencil, Trash2, Eye, Network, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { ROLE_GROUPS } from '@/types/auth';
import type { Service } from '@/types/service';

interface ServiceActionsProps {
  service: Service;
  onEdit?: (service: Service) => void;
  onDelete?: (service: Service) => void;
}

/**
 * Spec 352 §5: the shared view/routing/edit/deactivate action menu, lifted out of
 * ServiceCard so the card AND the table render edit/delete/view identically (both
 * stay wired to the ServiceForm modal + DeleteConfirmation in the parent page).
 * Stateless — the dialog state lives in the parent; this only fires the callbacks.
 *
 * stopPropagation guards the menu against the clickable table row's onClick.
 */
export function ServiceActions({ service, onEdit, onDelete }: ServiceActionsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('services');
  const { hasRole } = useAuth();
  // Spec 505/510: the Payment-Link wizard entry — since Spec 510 open to PM + merchant_it
  // (:own; the BFF enforces the tenant boundary server-side), ops/viewer stay out
  // (dead-end avoidance). No status query here — the Dropdown item navigates; status
  // lives on the target page (N+1 avoidance, IA §1).
  const canPaymentLink = hasRole(ROLE_GROUPS.PAYMENT_LINK_SERVICE_ACCESS);

  const go = (path: string) => navigate(path, { state: { merchantId: service.merchantId } });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 coarse:min-h-11 coarse:min-w-11"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">{t('common:aria.openActions')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => go(`/services/${service.serviceId}`)}>
          <Eye className="mr-2 h-4 w-4" />
          {t('common:label.view')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => go(`/services/${service.serviceId}/routing`)}>
          <Network className="mr-2 h-4 w-4" />
          {t('routing:detailCard.buttonEdit')}
        </DropdownMenuItem>
        {canPaymentLink && (
          <DropdownMenuItem onClick={() => go(`/services/${service.serviceId}/payment-link`)}>
            <Link2 className="mr-2 h-4 w-4" />
            {t('payment-link:entry.menuItem')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onEdit?.(service)}>
          <Pencil className="mr-2 h-4 w-4" />
          {t('common:button.edit')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onDelete?.(service)}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t('common:button.deactivate')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
